import { useState } from 'react';
import type { FeedCardProps } from './feedTypes.js';
import { getAuthHeaders } from '../../api/auth.js';
import './ObjectDetailCard.css';

interface ObjectDetailFeedItem {
  id: string;
  type: 'object-detail';
  title: string;
  config: {
    object: Record<string, unknown>;
    objectType?: string;
    objectId?: string;
    recommendedActions?: Array<{
      label: string;
      command: string;
      description?: string;
    }>;
  };
}

export function ObjectDetailCard({ item, onAction }: FeedCardProps<ObjectDetailFeedItem>) {
  const { object, objectType, objectId, recommendedActions = [] } = item.config;
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  // Fields to exclude from display
  const excludeFields = ['_id', 'id', '__v', 'passwordHash', 'password', 'tokenVersion', 'refreshTokenId'];

  // Get displayable fields
  const fields = Object.entries(object)
    .filter(([key]) => !excludeFields.includes(key))
    .sort(([a], [b]) => a.localeCompare(b));

  const renderFieldValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : '[]';
      }
      return JSON.stringify(value, null, 2);
    }
    if (typeof value === 'number') return value.toLocaleString('de-DE');
    return String(value);
  };

  const handleFieldClick = (fieldName: string, currentValue: unknown) => {
    setEditingField(fieldName);
    setEditValue(renderFieldValue(currentValue));
  };

  const handleFieldSave = (fieldName: string) => {
    if (onAction) {
      const objectName = object.displayName || object.name || object.tenantName || objectId || 'objekt';
      const command = `ändere ${objectType || 'objekt'} ${objectName} ${fieldName} auf ${editValue}`;
      onAction({ id: 'send-message', type: 'send-message', payload: { message: command } });
    }
    setEditingField(null);
  };

  const handleFieldCancel = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleActionClick = (command: string) => {
    // Handle certificate commands
    if (command.startsWith('CERTIFICATE_')) {
      const [action, connectorId] = command.split(':');
      handleCertificateAction(action, connectorId);
      return;
    }

    // Handle regular commands
    if (onAction) {
      onAction({ id: 'send-message', type: 'send-message', payload: { message: command } });
    }
  };

  const handleCertificateAction = async (action: string, connectorId: string) => {
    try {
      switch (action) {
        case 'CERTIFICATE_GENERATE':
        case 'CERTIFICATE_RENEW': {
          if (!confirm(`Möchten Sie wirklich ein ${action === 'CERTIFICATE_RENEW' ? 'neues' : ''} Zertifikat generieren?`)) {
            return;
          }

          const response = await fetch(`/api/hardware/connectors/${connectorId}/certificate`, {
            method: 'POST',
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ validityYears: 10 })
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API error: ${response.status} - ${errorText}`);
          }

          const result = await response.json();
          alert(`Zertifikat erfolgreich generiert!\n\nFingerprint: ${result.certificateFingerprint.substring(0, 16)}...\nGültig bis: ${new Date(result.validUntil).toLocaleDateString('de-DE')}\n\nJetzt herunterladen?`);

          // Refresh the view
          if (onAction) {
            onAction({ id: 'refresh', type: 'command', target: 'refresh', paramKey: 'current' });
          }
          break;
        }

        case 'CERTIFICATE_DOWNLOAD': {
          const response = await fetch(`/api/hardware/connectors/${connectorId}/certificate/download`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API error: ${response.status} - ${errorText}`);
          }

          const config = await response.json();

          // Trigger download
          const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `connector-${config.connectorId}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          alert('Zertifikat-Konfiguration erfolgreich heruntergeladen!');
          break;
        }

        case 'CERTIFICATE_REVOKE': {
          if (!confirm('WARNUNG: Zertifikat widerrufen?\n\nDer Connector kann sich nicht mehr verbinden, bis ein neues Zertifikat generiert wird.')) {
            return;
          }

          const response = await fetch(`/api/hardware/connectors/${connectorId}/certificate`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include'
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API error: ${response.status} - ${errorText}`);
          }

          alert('Zertifikat erfolgreich widerrufen!');

          // Refresh the view
          if (onAction) {
            onAction({ id: 'refresh', type: 'command', target: 'refresh', paramKey: 'current' });
          }
          break;
        }

        default:
          console.warn('Unknown certificate action:', action);
      }
    } catch (error) {
      console.error('Certificate action failed:', error);
      alert(`Fehler: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
    }
  };

  return (
    <div className="object-detail-card">
      <div className="object-detail-header">
        <h2 className="object-detail-title">{item.title}</h2>
        {objectType && (
          <span className="object-detail-type">{objectType}</span>
        )}
      </div>

      <div className="object-detail-attributes">
        {fields.map(([fieldName, fieldValue]) => {
          const isEditing = editingField === fieldName;

          return (
            <div
              key={fieldName}
              className={`object-detail-attribute ${isEditing ? 'editing' : ''}`}
              onClick={() => !isEditing && handleFieldClick(fieldName, fieldValue)}
            >
              <div className="attribute-label">{fieldName}</div>
              {isEditing ? (
                <div className="attribute-edit">
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleFieldSave(fieldName);
                      if (e.key === 'Escape') handleFieldCancel();
                    }}
                    autoFocus
                    className="attribute-input"
                  />
                  <div className="attribute-actions">
                    <button onClick={() => handleFieldSave(fieldName)} className="btn-save">✓</button>
                    <button onClick={handleFieldCancel} className="btn-cancel">✗</button>
                  </div>
                </div>
              ) : (
                <div className="attribute-value">{renderFieldValue(fieldValue)}</div>
              )}
            </div>
          );
        })}
      </div>

      {recommendedActions.length > 0 && (
        <div className="object-detail-actions">
          <h3 className="actions-title">Empfohlene Aktionen</h3>
          <div className="actions-list">
            {recommendedActions.map((action, index) => (
              <button
                key={index}
                className="action-button"
                onClick={() => handleActionClick(action.command)}
                title={action.description}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
